/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Element } from 'react';

import React from 'react';
import { Form, FormGroup, Label, Col, Row, UncontrolledAlert as Alert } from 'reactstrap';
import {
  BUILDING_TYPE_DIR_1,
  BUILDING_TYPE_DIR_2,
  DESIGN_CODE_YEAR,
  SEISMICITY_DESIGN,
  SEISMICITY_SHAKING,
  YEAR_OF_CONSTRUCTION,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import ContinueToNextSectionGroupButton from '#components/pages/Models/Model/sections/support/buttons/ContinueToNextSectionGroupButton/index.jsx';
import SubmitButton from '#components/pages/Models/Model/sections/support/buttons/ModelFormSectionSubmitButton/index.jsx';
import { FieldFeedback } from '#components/support/forms/utility/index.js';
import YearOfConstructionOrCodeYear from '#components/pages/Models/Model/sections/support/forms/YearOfConstructionOrCodeYear/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import BuildingTypeSelectMenu from './BuildingTypeSelectMenu/index.jsx';
import OccupancyFieldset from './OccupancyFieldset/index.js';
import SeismicitySelectMenu from './SeismicitySelectMenu/index.jsx';
import StoriesFieldSet from './StoriesFieldSet/index.js';

type Props = {|
  error: string,
  form: string,
  group: string,
  heading: string,
  modelId: string,
  modelType: number,
  onBack: Function,
  onNext: Function,
  handleSubmit: Function,
  submitting: boolean,
  isJapan: boolean,
  showSeismicity: boolean,
|};

const PrimaryBuildingInfoSection = (props: Props): Element<'div'> => {
  const {
    error,
    form,
    group,
    heading,
    modelId,
    modelType,
    onBack,
    onNext,
    handleSubmit,
    submitting,
    isJapan,
    showSeismicity,
  } = props;

  const buildingTypeField = (name, label) => (
    <FormGroup>
      <Label htmlFor={name}>{label}</Label>
      <BuildingTypeSelectMenu form={form} id={name} isJapan={isJapan} />
      <FieldFeedback name={name} form={form} />
    </FormGroup>
  );

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate onSubmit={handleSubmit}>
        <FormGroup tag="fieldset">
          <legend>Building Information</legend>
          <Row>
            <Col lg="9">
              Structural System <Tip tipId="structuralSystem" />
            </Col>
          </Row>
          <Row>
            <Col lg="9" className="ml-3">
              {buildingTypeField(BUILDING_TYPE_DIR_1, 'Direction 1')}
            </Col>
          </Row>
          <Row>
            <Col lg="9" className="ml-3">
              {buildingTypeField(BUILDING_TYPE_DIR_2, 'Direction 2')}
            </Col>
          </Row>
          <FormGroup row>
            <Col lg="5">
              <YearOfConstructionOrCodeYear
                yearOfConstructionName={YEAR_OF_CONSTRUCTION}
                designCodeYearName={DESIGN_CODE_YEAR}
                form={form}
              />
            </Col>
          </FormGroup>
          {showSeismicity && (
            <>
              <FormGroup row>
                <Col lg="5">
                  <Label htmlFor={SEISMICITY_DESIGN}>
                    Design Code Level of Seismicity <Tip tipId={SEISMICITY_DESIGN} />
                  </Label>
                  <SeismicitySelectMenu form={form} name={SEISMICITY_DESIGN} />
                  <FieldFeedback name={SEISMICITY_DESIGN} form={form} />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Col lg="4">
                  <Label htmlFor={SEISMICITY_SHAKING}>
                    Shaking Level of Seismicity <Tip tipId={SEISMICITY_SHAKING} />
                  </Label>
                  <SeismicitySelectMenu form={form} name={SEISMICITY_SHAKING} />
                  <FieldFeedback name={SEISMICITY_SHAKING} form={form} />
                </Col>
              </FormGroup>
            </>
          )}
        </FormGroup>
        <StoriesFieldSet form={form} />
        <OccupancyFieldset form={form} />
        {error && !submitting ? <Alert color="danger">{error}</Alert> : ''}
        <BackButton onClick={onBack} />
        <SubmitButton modelType={modelType} modelId={modelId} />
        <ContinueToNextSectionGroupButton group={group} modelType={modelType} onClick={onNext} />
      </Form>
    </div>
  );
};

export default PrimaryBuildingInfoSection;
